/* eslint-disable camelcase */
import { z } from 'zod'

import type {
  GetMeetingObservabilityCohortsGroupByEnum,
  GetMeetingObservabilityParticipantSourceEnum,
} from './generated/api'
import { attributionLabelSourcesSchema, type AttributionSourceSelection } from './attributionLabelSources'
import { fetchAPI } from '.'

export type {
  AttributionLabelRule,
  AttributionLabelSource,
  AttributionLabelSources,
  AttributionSourceSelection,
} from './attributionLabelSources'
export {
  attributionLabelRules,
  attributionLabelSources,
  attributionLabelSourcesSchema,
  labelSourceRules,
} from './attributionLabelSources'

const count = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER)
const optionalCount = count.nullable()

export const attributionSummarySchema = z.object({
  meeting_count: count,
  measured_label_meetings: count,
  machine_labeled_speakers: count,
  active_speakers: count,
  fully_machine_labeled_meetings: count,
  measured_ax_meetings: count,
  measured_ax_capture_meetings: count.default(0),
  ax_capture_ms: count.default(0),
  ax_readable_ms: count.default(0),
  speech_ms: count,
  ax_covered_speech_ms: count,
  measured_word_meetings: count.default(0),
  words: count.default(0),
  machine_labeled_words: count.default(0),
  label_sources: attributionLabelSourcesSchema.nullable().default(null),
})

const trendSummarySchema = attributionSummarySchema
  .pick({
    meeting_count: true,
    machine_labeled_speakers: true,
    active_speakers: true,
    ax_capture_ms: true,
    ax_readable_ms: true,
    speech_ms: true,
    ax_covered_speech_ms: true,
    words: true,
    machine_labeled_words: true,
  })
  // versionTail's addSummaries names each field, so a field added here must be summed there too.
  .extend({
    speaker_chip_count: count.optional(),
    named_speaker_chip_count: count.optional(),
    measured_label_meetings: count.optional(),
    fully_machine_labeled_meetings: count.optional(),
  })
const trendPointSchema = z.object({ date: z.string(), summary: trendSummarySchema })

const overviewSchema = z.object({
  summary: attributionSummarySchema,
  trend: z.array(trendPointSchema),
  facets: z.record(z.string(), z.array(z.string())),
  freshness: z.object({
    latest_completed_at: z.string().datetime({ offset: true }).nullable(),
    latest_persisted_at: z.string().datetime({ offset: true }).nullable(),
  }),
})

const cohortsSchema = z.object({
  groups: z.array(z.object({ value: z.string(), summary: attributionSummarySchema })),
  truncated: z.boolean(),
})

export const meetingSpeakerLabelMeetingSchema = z.object({
  meeting_key: z.string(),
  viewer_meeting_id: z.guid().nullable().default(null),
  meeting_created_at: z.string().datetime({ offset: true }),
  completed_at: z.string().datetime({ offset: true }),
  platform: z.string(),
  os: z.string(),
  client: z.string(),
  calendar_event: z.string(),
  user_meeting_number: optionalCount.default(null),
  recorded_ms: optionalCount.default(null),
  backend_version: z.string().nullable(),
  app_version: z.string().nullable().default(null),
  invited_attendee_count: optionalCount,
  active_speakers: optionalCount,
  machine_labeled_speakers: optionalCount,
  speech_ms: optionalCount,
  ax_covered_speech_ms: optionalCount,
  ax_measurement_status: z.string(),
  ax_capture_ms: optionalCount.default(null),
  ax_readable_ms: optionalCount.default(null),
  ax_capture_status: z.string().default('unknown'),
  label_measurement_status: z.string(),
  annotated: z.boolean().nullable().default(null),
})

const meetingsSchema = z.object({
  items: z.array(meetingSpeakerLabelMeetingSchema),
  next_cursor: z.string().nullable(),
})

export type AttributionSummary = z.infer<typeof attributionSummarySchema>
export type AttributionOverview = z.infer<typeof overviewSchema>
export type MeetingSpeakerLabelMeeting = z.infer<typeof meetingSpeakerLabelMeetingSchema>
export type AttributionCohorts = z.infer<typeof cohortsSchema>

export const attributionDimensions = [
  'platform',
  'os',
  'client',
  'calendar_event',
  'participant_bucket',
  'user_meeting_bucket',
  'ax_bucket',
  'recording_length_bucket',
  'backend_version',
  'app_version',
] as const satisfies readonly GetMeetingObservabilityCohortsGroupByEnum[]

export const facetDimensions = [
  'backend_version',
  'app_version',
] as const satisfies readonly GetMeetingObservabilityCohortsGroupByEnum[]

export type AttributionDimension = (typeof attributionDimensions)[number]
export type AttributionFilters = Partial<Record<AttributionDimension, string>> & {
  start_at: string
  end_at: string
  participant_source: GetMeetingObservabilityParticipantSourceEnum
  negate?: AttributionDimension[]
}

export type MeetingSpeakerLabelMeetingFilters = AttributionFilters & Partial<AttributionSourceSelection>

const BASE_PATH = '/api/v1/internal/meeting-speaker-labels/observability'

const parseResponse = <T extends z.ZodTypeAny>(schema: T, data: unknown): z.infer<T> => {
  const result = schema.safeParse(data)
  if (result.success) return result.data
  console.warn('Speaker-label response schema mismatch', {
    issues: result.error.issues.map(({ path, code }) => ({
      path: path[0] === 'facets' ? ['facets', '*'] : path,
      code,
    })),
  })
  throw result.error
}

export async function getAttributionOverview(filters: AttributionFilters, signal?: AbortSignal) {
  const response = await fetchAPI<unknown>({
    method: 'GET',
    url: BASE_PATH,
    params: filters,
    paramsSerializer: { indexes: null },
    signal,
  })
  return parseResponse(overviewSchema, response.data)
}

export async function getAttributionCohorts(
  filters: AttributionFilters,
  groupBy: AttributionDimension,
  signal?: AbortSignal
) {
  const response = await fetchAPI<unknown>({
    method: 'GET',
    url: `${BASE_PATH}/cohorts`,
    params: { ...filters, group_by: groupBy },
    paramsSerializer: { indexes: null },
    signal,
  })
  return parseResponse(cohortsSchema, response.data)
}

export async function getMeetingSpeakerLabelMeetings(
  filters: MeetingSpeakerLabelMeetingFilters,
  cursor?: string,
  signal?: AbortSignal,
  limit = 25
) {
  const response = await fetchAPI<unknown>({
    method: 'GET',
    url: `${BASE_PATH}/meetings`,
    params: { ...filters, limit, ...(cursor ? { cursor } : {}) },
    paramsSerializer: { indexes: null },
    signal,
  })
  return parseResponse(meetingsSchema, response.data)
}

const groupedTrendSchema = z.object({
  group_by: z.array(z.enum(attributionDimensions)),
  groups: z.array(
    z.object({
      path: z.array(z.object({ dimension: z.enum(attributionDimensions), value: z.string() })),
      trend: z.array(trendPointSchema),
    })
  ),
  truncated: z.boolean(),
})
export type AttributionGroupedTrend = z.infer<typeof groupedTrendSchema>
export async function getAttributionGroupedTrend(
  filters: AttributionFilters,
  groupBy: AttributionDimension[],
  signal?: AbortSignal
) {
  const response = await fetchAPI<unknown>({
    method: 'GET',
    url: `${BASE_PATH}/trend`,
    params: { ...filters, group_by: groupBy },
    paramsSerializer: { indexes: null },
    signal,
    expectedStatuses: [404, 405],
  })
  return parseResponse(groupedTrendSchema, response.data)
}
